<template>
	<div :class="classes">
		<router-view />
		<Confirm />
		<Alert />

		<img
			v-if="demoMode && cursorImage == 'arrow'"
			src="@/assets/img/cursorDemo_default.svg"
			:style="cursorProps"
			class="cursor"
		/>
		<img
			v-if="demoMode && cursorImage == 'pointer'"
			src="@/assets/img/cursorDemo_pointer.svg"
			:style="cursorProps"
			class="cursor"
		/>
		<div
			v-if="demoMode"
			class="clickIndicator"
			ref="clickIndicator"
			:style="clickIndicatorProps"
		></div>
	</div>
</template>

<script setup lang="ts">
import { gsap } from "gsap/gsap-core";
import { ref, computed, watch, onMounted, onBeforeUnmount, useTemplateRef } from "vue";
import { useRoute } from "vue-router";
import { storeMain as useStoreMain } from "@/store/storeMain";
import { storeParams as useStoreParams } from "@/store/params/storeParams";
import Config from "./utils/Config";
import Alert from "./views/AlertView.vue";
import Confirm from "./views/Confirm.vue";

const route = useRoute();
const storeMain = useStoreMain();
const storeParams = useStoreParams();

const clickIndicator = useTemplateRef<HTMLElement>("clickIndicator");

const cursorImage = ref<"pointer" | "arrow">("arrow");
const mousePos = { x: 0, y: 0 };
const cursorOffset = { x: 0, y: 0 };
const cursorProps = ref({ left: "0px", top: "0px" });
const clickIndicatorProps = ref({ left: "0px", top: "0px" });

let requestAnimID = -1;
let resizeHandler: () => void;
let dragStartHandler: (e: MouseEvent) => boolean;
let mouseDownHandler: (e: MouseEvent) => boolean;
let mouseMoveHandler: (e: MouseEvent) => boolean;
let keyDownHandler: (e: KeyboardEvent) => void;

const demoMode = computed(() => Config.instance.DEMO_MODE && route.name != "overlay");
const dyslexicFont = computed(() => storeParams.appearance.dyslexicFont.value as boolean);
const adhdFont = computed(() => storeParams.appearance.adhdFont.value as boolean);

const classes = computed(() => {
	let res = ["app"];
	if (dyslexicFont.value === true) res.push("dyslexicFont");
	if (adhdFont.value === true) res.push("adhdFont");
	if (route.meta.overflow === true) res.push("overflow");
	if (demoMode.value === true) res.push("demoMode");
	res.push("messageSize_" + storeParams.appearance.defaultSize.value);
	return res;
});

onMounted(() => {
	resizeHandler = () => onWindowResize();
	window.addEventListener("resize", resizeHandler);
	onWindowResize();
	hideMainLoader();
});

onBeforeUnmount(() => {
	cancelAnimationFrame(requestAnimID);
	window.removeEventListener("resize", resizeHandler);
	if (demoMode.value) {
		window.removeEventListener("mousedown", mouseDownHandler);
		window.removeEventListener("mousemove", mouseMoveHandler, true);
		window.removeEventListener("dragover", mouseMoveHandler);
		window.removeEventListener("dragstart", dragStartHandler);
		window.removeEventListener("keydown", keyDownHandler);
	}
});

function onWindowResize(): void {
	//vh metric is fucked up on mobile. It doesn't take header/footer UIs into account.
	//Here we calculate the actual page height and set it as a CSS var.
	(document.querySelector(":root") as HTMLHtmlElement).style.setProperty(
		"--vh",
		window.innerHeight + "px",
	);
}

function onKeyDown(e: KeyboardEvent): void {
	//Debuging stuff
	/*
	if(e.key == "a" && e.ctrlKey && e.altKey) {
		EventSub.instance.simulateFollowbotRaid();
		// for (let i = 0; i < 30; i++) {
		// 	Utils.promisedTimeout(i*100).then(()=>{
		// 		if(Math.random() >= .5) {
		// 			MessengerProxy.instance.sendMessage("/fake !q {LOREM}")
		// 		}else{
		// 			MessengerProxy.instance.sendMessage("/fake !howto {LOREM}")
		// 		}
		// 	})
		// }
	}
	if(e.key == "q" && e.ctrlKey && e.altKey) {
		MessengerProxy.instance.stopSpam();
	}
	//*/
}

function onMouseDown(e: MouseEvent): boolean {
	if (!demoMode.value) return false;
	gsap.fromTo(clickIndicator.value!, { scale: 0, opacity: 1 }, { scale: 1, opacity: 0 });
	clickIndicatorProps.value.left = mousePos.x + "px";
	clickIndicatorProps.value.top = mousePos.y - 2 + "px";
	return true;
}

function onDragStart(e: MouseEvent): boolean {
	//Avoid showing cursor on drag when using demo mode
	return false;
}

function onMouseMove(e: DragEvent | MouseEvent): boolean {
	if (!demoMode.value) return false;
	mousePos.x = e.clientX;
	mousePos.y = e.clientY;
	let target: HTMLElement | null = e.target as HTMLElement;
	let isButton = false;

	// console.log(target);
	while (target) {
		// console.log(target.style);
		// console.log(target.computedStyleMap().get("cursor"));
		if (
			target.tagName == "BUTTON" ||
			target.tagName == "A" ||
			target.classList.contains("checkbox") ||
			target.classList.contains("buttonnotification") ||
			target.classList.contains("switchbutton") ||
			target.classList.contains("ToggleButton.vue") ||
			target.classList.contains("button") ||
			target.classList.contains("toggle") ||
			(target.classList.contains("header") &&
				(target.parentElement as HTMLElement).classList.contains("toggleblock")) ||
			target.classList.contains("timercountdowninfo") ||
			target.classList.contains("mx-context-menu-item-wrapper") ||
			(typeof target.className === "string" && target.className.indexOf("Bt") > -1)
		) {
			isButton = true;
			break;
		}
		target = target.parentElement as HTMLElement;
		if (target === document.body) target = null;
	}

	if (isButton) {
		cursorOffset.x = -20;
		cursorImage.value = "pointer";
	} else {
		cursorOffset.x = 0;
		cursorImage.value = "arrow";
	}
	return false;
}

function hideMainLoader(): void {
	if (storeMain.initComplete === true) {
		//@ts-ignore
		closeInitLoader(); //Method declared on index.html
	}
}

function renderFrame(): void {
	requestAnimID = requestAnimationFrame(() => renderFrame());
	cursorProps.value.left = mousePos.x + cursorOffset.x + "px";
	cursorProps.value.top = mousePos.y - 2 + "px";
}

watch(
	() => storeMain.initComplete,
	() => hideMainLoader(),
);
watch(
	() => Config.instance.DEMO_MODE,
	() => {
		if (demoMode.value) {
			document.body.classList.add("demoMode");
			renderFrame();
			mouseMoveHandler = (e) => onMouseMove(e);
			dragStartHandler = (e) => onDragStart(e);
			mouseDownHandler = (e) => onMouseDown(e);
			keyDownHandler = (e) => onKeyDown(e);
			window.addEventListener("mousedown", mouseDownHandler);
			window.addEventListener("mousemove", mouseMoveHandler, true);
			window.addEventListener("dragover", mouseMoveHandler);
			window.addEventListener("dragstart", dragStartHandler);
			window.addEventListener("keydown", keyDownHandler);
		} else {
			document.body.classList.remove("demoMode");
			cancelAnimationFrame(requestAnimID);
			window.removeEventListener("mousedown", mouseDownHandler);
			window.removeEventListener("mousemove", mouseMoveHandler, true);
			window.removeEventListener("dragover", mouseMoveHandler);
			window.removeEventListener("dragstart", dragStartHandler);
			window.removeEventListener("keydown", keyDownHandler);
		}
	},
);
</script>

<style scoped lang="less">
.app {
	width: 100%;
	height: var(--vh);
	font-family: var(--font-inter);
	overflow: hidden;

	// &.demoMode {
	// max-width: var(--vw) !important;
	// width: var(--vw) !important;
	// }

	.cursor {
		pointer-events: none;
		position: fixed;
		width: 50px;
		z-index: 99999;
	}
	.clickIndicator {
		z-index: 99998;
		pointer-events: none;
		top: 0;
		left: 0;
		position: fixed;
		background-color: var(--color-text);
		border-radius: 50%;
		width: 100px;
		height: 100px;
		transform: translate(-50%, -50%) scale(0);
	}

	&.dyslexicFont {
		font-size: 0.9em;
		--font-inter: "OpenDyslexic";
		--font-nunito: "OpenDyslexic";
		--font-roboto: "OpenDyslexic";
		--font-azeret: "OpenDyslexic";
	}

	&.adhdFont {
		--font-inter: "FastMono";
		--font-nunito: "FastMono";
		--font-roboto: "FastMono";
		--font-azeret: "FastMono";
	}

	&.overflow {
		overflow: auto;
	}

	&.messageSize_1 {
		--messageSize: 0.6em;
	}
	&.messageSize_2 {
		--messageSize: 0.7em;
	}
	&.messageSize_3 {
		--messageSize: 0.8em;
	}
	&.messageSize_4 {
		--messageSize: 0.9em;
	}
	&.messageSize_5 {
		--messageSize: 1em;
	}
	&.messageSize_6 {
		--messageSize: 1.1em;
	}
	&.messageSize_7 {
		--messageSize: 1.2em;
	}
	&.messageSize_8 {
		--messageSize: 1.3em;
	}
	&.messageSize_9 {
		--messageSize: 1.4em;
	}
	&.messageSize_10 {
		--messageSize: 1.5em;
	}
	&.messageSize_11 {
		--messageSize: 1.6em;
	}
	&.messageSize_12 {
		--messageSize: 1.7em;
	}
	&.messageSize_13 {
		--messageSize: 1.8em;
	}
	&.messageSize_14 {
		--messageSize: 1.9em;
	}
	&.messageSize_15 {
		--messageSize: 2em;
	}
	&.messageSize_16 {
		--messageSize: 2.1em;
	}
	&.messageSize_17 {
		--messageSize: 2.2em;
	}
	&.messageSize_18 {
		--messageSize: 2.3em;
	}
	&.messageSize_19 {
		--messageSize: 2.4em;
	}
	&.messageSize_20 {
		--messageSize: 2.5em;
	}
}
</style>
